import { Injectable } from '@angular/core';
import { Clinic, Invitation, Vet, VetUser } from '@app/core/models';
import {
  selectCanShareRecoByEmail,
  selectClinic,
  selectClinics,
  selectCurrentClinicCountry,
  selectCurrentClinicCurrency,
  selectCurrentClinicId,
  selectIsClinicAddressComplete,
  selectConversionConfiguration,
  selectIsRetailPriceActivated,
  selectIsSolProductsInVetEnable,
  selectIsSptRetailEnable,
  selectIsVetValid,
  selectIsWholesalePriceActivated,
  selectPreferredLanguage,
  selectMarket,
  selectMarketQuicklinks,
  selectSystemPreference,
  selectOktaUser,
  selectVet,
  selectWholesalePrices,
  selectIsInvitationOnboardingEnable,
  selectMarketMailLanguage,
  selectCanShareRecoByQrcode,
  selectCanShareRecoByPrint,
  selectHideDietBlockInPoLetter,
  selectCompanyAddress,
  selectIsIndividualisEnabled,
  selectIsGrowthChartEnabled,
  selectIsVetSourceEnabled,
  selectIsPostSterilizationEnabled,
  selectIsSrCriteriaGenericEnabled,
  selectIsClinicalAllianceEnabled,
  selectPreferredMaxPetWeight,
  selectCurrentBigMeasurementUnit,
  selectCurrentSmallMeasurementUnit,
  selectVetId,
  selectCurrentSystemPreferenceCode,
  selectIsTreatsEnabled,
  selectIsPriceUploadEnabled,
  selectIsEBoutiqueLinksEnabled,
  selectHasRecoSharingConsent,
  selectIsKpiFileEnable,
  selectIsFeatureEnabled,
} from '@app/store/vet/vet.selectors';
import { select, Store } from '@ngrx/store';
import { switchMap, tap } from 'rxjs/operators';
import { AppState } from '..';
import {
  displayInfoPopin,
  getVet,
  setCurrentBigMeasurementUnit,
  setCurrentClinicId,
  setCurrentSmallMeasurementUnit,
  setCurrentSystemPreferenceCode,
  setInvitation,
  setPreferredMaxPetWeight,
  setVet,
  setVetValidity,
  updateClinic,
  updateVet,
  updateVetUser,
} from './vet.actions';
import { MeasurementCodeType } from '@app/shared/utils';
import { SystemPreferenceCode } from '@app/shared/utils/enums/system-preference-code';
import { FeatureToggle } from '@app/core/models/feature-toggles';

@Injectable()
export class VetFacade {
  clinicId: string;

  /**
   * Vet selectors
   */
  vet$ = this.store$.pipe(select(selectVet));
  vetId$ = this.store$.select(selectVetId);
  isVetValid$ = this.store$.pipe(select(selectIsVetValid));
  preferredLanguage$ = this.store$.pipe(select(selectPreferredLanguage));
  systemPreference$ = this.store$.pipe(select(selectSystemPreference));
  companyAddress$ = this.store$.pipe(select(selectCompanyAddress));

  /**
   * Okta user selector
   */
  oktaUser$ = this.store$.pipe(select(selectOktaUser));

  /**
   * Reco sharing consent selector
   */
  hasRecoSharingConsent$ = this.store$.pipe(select(selectHasRecoSharingConsent));

  /**
   * Clinic selectors
   */
  currentClinicId$ = this.store$.pipe(select(selectCurrentClinicId));
  clinics$ = this.store$.pipe(select(selectClinics));
  clinic$ = this.store$.pipe(select(selectClinic)).pipe(tap((clinic: Clinic) => (this.clinicId = clinic?.id)));
  currentClinicCountry$ = this.store$.pipe(select(selectCurrentClinicCountry));
  isClinicAddressComplete$ = this.store$.pipe(select(selectIsClinicAddressComplete));
  isIndividualisEnabled$ = this.store$.pipe(select(selectIsIndividualisEnabled));
  isGrowthChartEnabled$ = this.store$.pipe(select(selectIsGrowthChartEnabled));
  isPostSterilizationEnabled$ = this.store$.pipe(select(selectIsPostSterilizationEnabled));
  isSrCriteriaGenericEnabled$ = this.store$.pipe(select(selectIsSrCriteriaGenericEnabled));
  isClinicalAllianceEnabled$ = this.store$.select(selectIsClinicalAllianceEnabled);
  isTreatsEnabled$ = this.store$.select(selectIsTreatsEnabled);
  isPriceUploadEnabled$ = this.store$.select(selectIsPriceUploadEnabled);
  isEBoutiqueLinksEnabled$ = this.store$.select(selectIsEBoutiqueLinksEnabled);
  isVetSourceEnabled$ = this.clinic$.pipe(switchMap(({ id }) => this.store$.select(selectIsVetSourceEnabled(id))));

  /**
   * Feature toggle selectors
   */
  isFeatureEnabled$ = (feature: FeatureToggle) => this.store$.select(selectIsFeatureEnabled(feature));

  /**
   * Market selectors
   */
  market$ = this.store$.pipe(select(selectMarket));
  marketQuicklinks$ = this.store$.pipe(select(selectMarketQuicklinks));
  canShareRecoByEmail$ = this.store$.pipe(select(selectCanShareRecoByEmail));
  canShareRecoByQrcode$ = this.store$.pipe(select(selectCanShareRecoByQrcode));
  canShareRecoByPrint$ = this.store$.pipe(select(selectCanShareRecoByPrint));
  isRetailPriceActivated$ = this.store$.pipe(select(selectIsRetailPriceActivated));
  isWholesalePriceActivated$ = this.store$.pipe(select(selectIsWholesalePriceActivated));
  wholesalePrices$ = this.store$.pipe(select(selectWholesalePrices));
  enableSptRetail$ = this.store$.pipe(select(selectIsSptRetailEnable));
  enableSolProductsInVet$ = this.store$.pipe(select(selectIsSolProductsInVetEnable));
  enableKpiFile$ = this.store$.pipe(select(selectIsKpiFileEnable));
  enableInvitationOnboarding$ = this.store$.pipe(select(selectIsInvitationOnboardingEnable));
  marketMailLanguage$ = this.store$.pipe(select(selectMarketMailLanguage));
  hideDietBlockInPoLetter$ = this.store$.pipe(select(selectHideDietBlockInPoLetter));

  /**
   * Clinic & Market selectors
   */
  currentClinicCurrency$ = this.store$.pipe(select(selectCurrentClinicCurrency));
  conversionConfiguration$ = this.store$.pipe(select(selectConversionConfiguration));

  /**
   * Preferences selectors
   */
  preferredMaxPetWeight$ = this.store$.select(selectPreferredMaxPetWeight);
  currentBigMeasurementUnit$ = this.store$.select(selectCurrentBigMeasurementUnit);
  currentSmallMeasurementUnit$ = this.store$.select(selectCurrentSmallMeasurementUnit);
  currentSystemPreferenceCode$ = this.store$.select(selectCurrentSystemPreferenceCode);

  constructor(private readonly store$: Store<AppState>) {}

  setVet(vet: Vet) {
    this.store$.dispatch(setVet({ vet }));
  }

  updateVet(vet: Vet, reload?: boolean): void {
    this.store$.dispatch(updateVet({ vet, reload }));
  }

  updateVetUser(oktaId: string, vetUser: VetUser) {
    this.store$.dispatch(updateVetUser({ oktaId, vetUser }));
  }

  setCurrentClinicId(clinicId: string) {
    this.store$.dispatch(setCurrentClinicId({ clinicId }));
  }

  getVet() {
    this.store$.dispatch(getVet());
  }

  /**
   * Set vet validity
   * See vet.effects for validity criteria
   */
  setVetValidity(value: boolean) {
    this.store$.dispatch(setVetValidity({ value }));
  }

  setInvitation(invitation: Invitation) {
    this.store$.dispatch(setInvitation({ invitation }));
  }

  updateClinic(clinicId: string, clinic: Partial<Clinic>, redirectTo?: string[]) {
    this.store$.dispatch(updateClinic({ clinicId, clinic, redirectTo }));
  }

  displayInfoPopin() {
    this.store$.dispatch(displayInfoPopin());
  }

  setPreferredMaxPetWeight(weight: number): void {
    this.store$.dispatch(setPreferredMaxPetWeight({ weight }));
  }

  setCurrentBigMeasurementUnit(unit: MeasurementCodeType): void {
    this.store$.dispatch(setCurrentBigMeasurementUnit({ unit }));
  }

  setCurrentSmallMeasurementUnit(unit: MeasurementCodeType): void {
    this.store$.dispatch(setCurrentSmallMeasurementUnit({ unit }));
  }

  setCurrentSystemPreferenceCode(systemPreferenceCode: SystemPreferenceCode): void {
    this.store$.dispatch(setCurrentSystemPreferenceCode({ systemPreferenceCode }));
  }
}
